import type { OperationalMetric } from "../../api/types";
import { formatCount, isValidCount } from "../../formatting/number";

/**
 * Pure, client-side derivations for `OverviewScreen` (REDISENO-PANEL.md §9.4
 * "Únicos valores derivados en el cliente" — tasa de entrega, porcentaje de
 * barras, fechas relativas y saludo según la hora, "programadas esta
 * semana"). No I/O, no React — every function here takes plain data/ISO
 * strings/epoch millis and returns a display-ready value. Kept separate from
 * `OverviewScreen.tsx` so this logic is unit-testable without rendering.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** §4: "saludo según la hora local: días / tardes / noches". */
export function greetingForHour(hour: number): string {
  if (hour < 12) return "Buenos días";
  if (hour < 19) return "Buenas tardes";
  return "Buenas noches";
}

/** §4: "H1 'Buenos días, {nombre}' ... nombre de la cuenta MSAL" — only the first token of the account's display name. */
export function firstName(fullName: string | null | undefined): string {
  if (!fullName) return "";
  const [first] = fullName.trim().split(/\s+/).filter(Boolean);
  return first ?? "";
}

const HEADER_DATE_FORMATTER = new Intl.DateTimeFormat("es-CO", { weekday: "long", day: "numeric", month: "long" });

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** §4: "línea secundaria con la fecha + 'actualizado HH:MM' (overview.generatedAt)". */
export function formatHeaderLine(generatedAtIso: string): string {
  const date = new Date(generatedAtIso);
  const dateLabel = HEADER_DATE_FORMATTER.format(date);
  const capitalized = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);
  return `${capitalized} · actualizado ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function startOfDayMs(ms: number): number {
  const date = new Date(ms);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/**
 * §4 "programadas esta semana" / §9.4: a scheduled date counts as "this
 * week" when it is less than 7 full days away from `nowMs` — mirrors the
 * prototype's own `c.at - NOW < 7*864e5` (no lower bound, so an overdue
 * `programada` still counts rather than silently vanishing from the ledger).
 */
export function isWithinNextSevenDays(targetIso: string, nowMs: number): boolean {
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return false;
  return target - nowMs < 7 * DAY_MS;
}

/**
 * Codex finding 1 (PR #382 round 4): "Próximos envíos" means genuinely
 * UPCOMING sends — an overdue `programada` row (past its own date, left that
 * way by a `CampaignDispatchQueueFunction` failure path) still belongs in
 * "Requiere tu atención" (`isWithinNextSevenDays` deliberately has no lower
 * bound for that card), but it must never occupy one of this card's limited
 * slots. An unparsable date is never "overdue" by this check — it already
 * sorts last via `compareScheduledDates` and gets excluded by the row limit
 * naturally instead.
 */
export function isOverdueSchedule(targetIso: string, nowMs: number): boolean {
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return false;
  return target < nowMs;
}

/** §4: relative day label ("hoy", "mañana", "en N días") for upcoming/attention rows, by calendar day (not raw elapsed hours). */
export function relativeDayLabel(targetIso: string, nowMs: number): string {
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return "sin fecha";
  const days = Math.round((startOfDayMs(target) - startOfDayMs(nowMs)) / DAY_MS);
  if (days === 0) return "hoy";
  if (days === 1) return "mañana";
  if (days === -1) return "ayer";
  return days > 0 ? `en ${days} días` : `hace ${-days} días`;
}

const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("es-CO", { day: "numeric", month: "short" });

/** §4: "borradores por programar" row — "nombre · fecha corta o 'Sin fecha'". */
export function formatShortDateOrNone(iso: string | null | undefined): string {
  if (!iso) return "Sin fecha";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return SHORT_DATE_FORMATTER.format(date);
}

const MONTH_ABBR = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

/**
 * §4: "Próximos envíos" date block — "mes en mayúsculas y día". Native-review
 * advisory o: an unparsable `iso` used to produce `{ month: "", day: "NaN" }`
 * — the literal string "NaN" rendered straight into the UI. Same "Sin fecha"
 * fallback convention `formatShortDateOrNone` already uses.
 */
export function monthDayParts(iso: string): { month: string; day: string } {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { month: "Sin fecha", day: "" };
  return { month: MONTH_ABBR[date.getMonth()] ?? "", day: String(date.getDate()) };
}

/**
 * §4: "HH:MM · relativo · show" meta line's time component — local
 * hour:minute, zero-padded. Native-review advisory o: an unparsable `iso`
 * used to render the literal "NaN:NaN" — same "Sin fecha" fallback
 * convention as `formatShortDateOrNone`/`monthDayParts`.
 */
export function formatTimeHHmm(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/**
 * §4 "Próximos envíos" sort comparator. Native-review advisory o: a bare
 * `new Date(a).getTime() - new Date(b).getTime()` produces NaN whenever
 * either side is unparsable, and `Array.prototype.sort` gives no ordering
 * guarantee for a NaN-returning comparator — an invalid `fechaProgramada`
 * could land anywhere instead of predictably last.
 */
export function compareScheduledDates(aIso: string, bIso: string): number {
  const aTime = new Date(aIso).getTime();
  const bTime = new Date(bIso).getTime();
  const aValid = !Number.isNaN(aTime);
  const bValid = !Number.isNaN(bTime);
  if (aValid && bValid) return aTime - bTime;
  if (aValid) return -1;
  if (bValid) return 1;
  return 0;
}

/** §0 ⚠ issue #330: `showName` can be `null` on a campaign row — degrade to the template name alone rather than a dangling separator. */
export function formatTemplateShowMeta(templateName: string, showName: string | null): string {
  return showName ? `${templateName} · ${showName}` : templateName;
}

/**
 * §4 "Próximos envíos" meta line — "HH:MM · relativo · show". Codex finding
 * 10 (PR #382): a `null` showName means the Show join is unavailable (§0 ⚠
 * issue #330), not "no show" — so the show segment is OMITTED entirely
 * rather than falling back to a literal "Sin show" placeholder, the same
 * "omit, don't invent a placeholder" convention `formatTemplateShowMeta`
 * already uses for LiveSection.
 */
export function formatUpcomingMeta(timeLabel: string, relativeLabel: string, showName: string | null): string {
  const base = `${timeLabel} · ${relativeLabel}`;
  return showName ? `${base} · ${showName}` : base;
}

/** Finds `key` in a metric catalog and returns its value only when `availability === "available"` (§9.2: never surface a value for a blocked/unavailable metric). */
export function metricValue(metrics: readonly OperationalMetric[], key: string): number | null {
  const found = metrics.find((metric) => metric.key === key);
  if (!found || found.availability !== "available" || !isValidCount(found.value)) return null;
  return found.value;
}

/** §9.2: "si una métrica es unavailable, mostrar '—' y nunca un 0" — a real measured zero still renders as "0". */
export function formatMetricDisplay(metrics: readonly OperationalMetric[], key: string): string {
  const value = metricValue(metrics, key);
  return value === null ? "—" : formatCount(value);
}

const RATE_FORMATTER = new Intl.NumberFormat("es-CO", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** §9.4: "Tasa de entrega = entregado ÷ encolado (1 decimal, '—' si encolado = 0)" — also '—' when either figure is unavailable. */
export function formatDeliveryRateFromMetrics(metrics: readonly OperationalMetric[]): string {
  const encolado = metricValue(metrics, "encolado");
  const entregado = metricValue(metrics, "entregado");
  if (encolado === null || entregado === null || encolado === 0) return "—";
  // Codex finding 11 (PR #382 review round 3): in-flight aggregates are
  // explicitly not guaranteed to be internally consistent (e.g. 12
  // `entregado` events for 10 `encolado` events) — the adjacent
  // `DeliveryProgressBar` already clamps its own delivered value to the
  // total; clamp this percentage the same way so the two never contradict
  // each other with an impossible rate like "120,0 %".
  const rate = Math.min((entregado / encolado) * 100, 100);
  return `${RATE_FORMATTER.format(rate)} %`;
}

/**
 * progress-bar-sent: sent percentage (enviados ÷ encolado, 1 decimal, clamped to 100) for
 * the "Enviando ahora" rows — "—" while `enviados` or `encolado` is missing/invalid or encolado is 0.
 */
export function formatSentRate(enviados: number | null, encolado: number | null): string {
  if (enviados === null || encolado === null || !isValidCount(enviados) || !isValidCount(encolado) || encolado === 0) {
    return "—";
  }
  return `${RATE_FORMATTER.format(Math.min((enviados / encolado) * 100, 100))} %`;
}
